import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Crown, Check, Loader } from 'lucide-react';
import { Button } from '../components/Button';
import { Card } from '../components/Card';
import { useAuth } from '../contexts/AuthContext';
import { notifications } from '../lib/notifications';
export function UpgradePage() {
  const navigate = useNavigate();
  const {
    user
  } = useAuth();
  const [loading, setLoading] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<'monthly' | 'yearly'>('yearly');
  const handleUpgrade = async () => {
    if (!user) {
      notifications.error('Please log in', 'You need to be logged in to upgrade');
      navigate('/login');
      return;
    }
    setLoading(true);
    try {
      // In production, this would call your backend to create a Stripe Checkout session
      // For now, we'll simulate the process
      console.log('Creating Stripe checkout session for:', {
        userId: user.uid,
        plan: selectedPlan,
        priceId: selectedPlan === 'monthly' ? 'price_monthly_id' : 'price_yearly_id'
      });
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      // In production, redirect to Stripe Checkout:
      // window.location.href = checkoutSession.url
      notifications.success('Redirecting to checkout...', 'Please complete your payment');
      // For demo purposes, show success
      setTimeout(() => {
        notifications.success('Payment successful!', 'Welcome to FlowQuest Premium!');
        navigate('/profile');
      }, 2000);
    } catch (error: any) {
      notifications.error('Upgrade failed', error.message);
    } finally {
      setLoading(false);
    }
  };
  const premiumFeatures = [{
    icon: '🎯',
    text: 'Unlimited quests'
  }, {
    icon: '👥',
    text: 'Unlimited collaborative quests'
  }, {
    icon: '🎨',
    text: 'Custom categories with icons'
  }, {
    icon: '🧘',
    text: 'Advanced focus modes (Pomodoro)'
  }, {
    icon: '🤖',
    text: 'AI-powered quest suggestions'
  }, {
    icon: '📊',
    text: 'Advanced analytics & insights'
  }, {
    icon: '📥',
    text: 'Export data (PDF, CSV)'
  }, {
    icon: '🎨',
    text: 'Custom themes'
  }, {
    icon: '📅',
    text: 'Calendar integration'
  }, {
    icon: '🔁',
    text: 'Recurring quests & habits'
  }, {
    icon: '👨‍👩‍👧‍👦',
    text: 'Team workspaces'
  }, {
    icon: '⚡',
    text: 'Priority support'
  }];
  return <div className="min-h-screen bg-game-bg-pink dark:bg-[#1a1d29] pb-24" data-id="element-1271">
      {/* Header */}
      <header className="px-6 pt-6 pb-4 sticky top-0 bg-game-bg-pink/90 dark:bg-[#1a1d29]/90 backdrop-blur-sm z-10" data-id="element-1272">
        <div className="flex items-center gap-4 mb-4" data-id="element-1273">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="!p-2" data-id="element-1274">
            <ArrowLeft className="w-6 h-6" data-id="element-1275" />
          </Button>
          <div data-id="element-1276">
            <h1 className="text-2xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1277">
              Upgrade to Premium
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1278">
              Unlock your full potential
            </p>
          </div>
        </div>
      </header>

      <main className="px-6 space-y-6 max-w-4xl mx-auto" data-id="element-1279">
        {/* Hero Card */}
        <Card className="p-8 bg-gradient-to-br from-game-purple via-game-teal to-game-coral text-white relative overflow-hidden" data-id="element-1280">
          <div className="relative z-10" data-id="element-1281">
            <div className="flex items-center gap-3 mb-4" data-id="element-1282">
              <div className="w-16 h-16 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center" data-id="element-1283">
                <Crown className="w-8 h-8 text-white" data-id="element-1284" />
              </div>
              <div data-id="element-1285">
                <h2 className="text-3xl font-fredoka font-bold" data-id="element-1286">
                  FlowQuest Premium
                </h2>
                <p className="font-quicksand opacity-90" data-id="element-1287">
                  Supercharge your productivity
                </p>
              </div>
            </div>
            <p className="font-quicksand text-lg opacity-90 leading-relaxed" data-id="element-1288">
              Get unlimited quests, advanced features, and AI-powered insights
              to achieve your goals faster than ever.
            </p>
          </div>
          {/* Decorative circles */}
          <div className="absolute -top-10 -right-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" data-id="element-1289"></div>
          <div className="absolute -bottom-10 -left-10 w-40 h-40 bg-white/10 rounded-full blur-2xl" data-id="element-1290"></div>
        </Card>

        {/* Pricing Plans */}
        <div className="grid md:grid-cols-2 gap-4" data-id="element-1291">
          {/* Monthly Plan */}
          <Card className={`p-6 cursor-pointer transition-all ${selectedPlan === 'monthly' ? 'ring-2 ring-game-purple bg-game-bg-purple dark:bg-purple-900/20' : ''}`} onClick={() => setSelectedPlan('monthly')} data-id="element-1292">
            <div className="flex items-start justify-between mb-4" data-id="element-1293">
              <div data-id="element-1294">
                <h3 className="text-xl font-fredoka font-bold text-game-dark dark:text-white mb-1" data-id="element-1295">
                  Monthly
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1296">
                  Billed monthly
                </p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'monthly' ? 'border-game-purple bg-game-purple' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-1297">
                {selectedPlan === 'monthly' && <Check className="w-4 h-4 text-white" data-id="element-1298" />}
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-2" data-id="element-1299">
              <span className="text-4xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1300">
                $9.99
              </span>
              <span className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1301">
                /month
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-quicksand" data-id="element-1302">
              Cancel anytime
            </p>
          </Card>

          {/* Yearly Plan */}
          <Card className={`p-6 cursor-pointer transition-all relative ${selectedPlan === 'yearly' ? 'ring-2 ring-game-purple bg-game-bg-purple dark:bg-purple-900/20' : ''}`} onClick={() => setSelectedPlan('yearly')} data-id="element-1303">
            <div className="absolute -top-3 right-4 bg-gradient-to-r from-game-coral to-game-yellow px-3 py-1 rounded-full" data-id="element-1304">
              <span className="text-xs font-fredoka font-bold text-white" data-id="element-1305">
                SAVE 33%
              </span>
            </div>
            <div className="flex items-start justify-between mb-4" data-id="element-1306">
              <div data-id="element-1307">
                <h3 className="text-xl font-fredoka font-bold text-game-dark dark:text-white mb-1" data-id="element-1308">
                  Yearly
                </h3>
                <p className="text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1309">
                  Billed annually
                </p>
              </div>
              <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${selectedPlan === 'yearly' ? 'border-game-purple bg-game-purple' : 'border-gray-300 dark:border-gray-600'}`} data-id="element-1310">
                {selectedPlan === 'yearly' && <Check className="w-4 h-4 text-white" data-id="element-1311" />}
              </div>
            </div>
            <div className="flex items-baseline gap-2 mb-2" data-id="element-1312">
              <span className="text-4xl font-fredoka font-bold text-game-dark dark:text-white" data-id="element-1313">
                $79.99
              </span>
              <span className="text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1314">
                /year
              </span>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 font-quicksand" data-id="element-1315">
              Just $6.67/month • Save $40/year
            </p>
          </Card>
        </div>

        {/* Features List */}
        <Card className="p-6" data-id="element-1316">
          <h3 className="font-fredoka font-bold text-xl text-game-dark dark:text-white mb-6" data-id="element-1317">
            Everything included:
          </h3>
          <div className="grid md:grid-cols-2 gap-4" data-id="element-1318">
            {premiumFeatures.map((feature, index) => <div key={index} className="flex items-center gap-3" data-id="element-1319">
                <span className="text-2xl" data-id="element-1320">{feature.icon}</span>
                <span className="font-quicksand text-gray-700 dark:text-gray-300" data-id="element-1321">
                  {feature.text}
                </span>
              </div>)}
          </div>
        </Card>

        {/* CTA Button */}
        <Button onClick={handleUpgrade} disabled={loading} className="w-full bg-gradient-to-r from-game-purple to-game-teal" size="lg" icon={loading ? <Loader className="w-5 h-5 animate-spin" data-id="element-1323" /> : <Crown className="w-5 h-5" data-id="element-1324" />} data-id="element-1322">
          {loading ? 'Processing...' : `Upgrade to ${selectedPlan === 'monthly' ? 'Monthly' : 'Yearly'} Plan`}
        </Button>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-500 dark:text-gray-400 font-quicksand" data-id="element-1325">
          <div className="flex items-center gap-2" data-id="element-1326">
            <Check className="w-4 h-4 text-game-teal" data-id="element-1327" />
            <span data-id="element-1328">Cancel anytime</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1329">
            <Check className="w-4 h-4 text-game-teal" data-id="element-1330" />
            <span data-id="element-1331">Secure payment via Stripe</span>
          </div>
          <div className="flex items-center gap-2" data-id="element-1332">
            <Check className="w-4 h-4 text-game-teal" data-id="element-1333" />
            <span data-id="element-1334">30-day money back guarantee</span>
          </div>
        </div>
      </main>
    </div>;
}